import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {isTopLevel} from '@apps-common/auth/client.js';

/**
 * @typedef {Object} AuthState
 * @property {boolean} pending Whether the auth state is pending.
 * @property {import('@apps-common/auth/client.js').User|null} user The user info.
 * @property {Error|null} error Any error (or null if none).
 * @property {function(string=): Promise<void>} signIn Log in the user.
 * @property {function(): Promise<void>} signOut Log out the user.
 * @property {function(): Promise<void>} renew Renew tokens.
 */

/**
 * @type {AuthState}
 */
const defaultAuthState = {
  pending: true,
  user: null,
  error: null,
  signIn: async () => {},
  signOut: async () => {},
  renew: async () => {},
};

const Context = createContext(defaultAuthState);

/**
 * Monitor the auth state.
 * @return {AuthState} The auth state.
 */
export function useAuth() {
  return useContext(Context);
}

/**
 * @typedef {Object} AuthProviderProps
 * @property {import('@apps-common/auth').Client} client The auth client.
 * @property {function(string): void} [navigate] The navigate function.
 * @property {boolean} [private] Only render children if the user is logged in.
 * @property {import('@apps-common/auth/client.js').ChangeListener} [onChange] Called with changes to the authentication state.
 * @property {import('react').ReactNode} [errorElement] An element to render if there is an error authenticating.
 * @property {import('react').ReactNode} children The children.
 */

/**
 * A provider for the auth state.
 * @param {AuthProviderProps} props The props.
 */
export function AuthProvider({
  client,
  children,
  private: isPrivate,
  navigate,
  onChange,
  errorElement,
}) {
  if (navigate) {
    client.setNavigate(navigate);
  }
  const signIn = useCallback(() => client.signIn(), [client]);
  const signOut = useCallback(() => client.signOut(), [client]);
  const renew = useCallback(() => client.renew(), [client]);

  const clientState = client.getState();
  const initial = useRef(true);
  if (onChange && initial.current) {
    initial.current = false;
    onChange(clientState);
  }

  /**
   * @type {AuthState}
   */
  const authState = {...clientState, signIn, signOut, renew};
  const [state, setState] = useState(authState);

  /**
   * @type {import('@apps-common/auth/client.js').ChangeListener}
   */
  const handleClientChange = useCallback(
    clientState => {
      if (onChange) {
        onChange(clientState);
      }
      setState({...clientState, signIn, signOut, renew});
    },
    [onChange, renew, signIn, signOut],
  );

  useEffect(() => {
    client.addChangeListener(handleClientChange);
    return () => client.removeChangeListener(handleClientChange);
  }, [client, handleClientChange]);

  useEffect(() => {
    if (!isPrivate || state.user || state.pending || state.error) {
      return;
    }
    signIn();
  }, [isPrivate, signIn, state.error, state.pending, state.user]);

  if (state.error) {
    return createElement(
      Context.Provider,
      {value: state},
      errorElement || createElement(DefaultErrorElement),
    );
  }

  if ((isPrivate && !state.user) || !isTopLevel()) {
    return null;
  }

  return createElement(Context.Provider, {value: state}, children);
}

function DefaultErrorElement() {
  const {error} = useAuth();
  return createElement('div', null, String(error));
}

/**
 * @typedef {Object} PrivateProps
 * @property {import('react').ReactNode} children The children.
 */

/**
 * Renders children only if the user is authenticated.  Initiates the sign in flow otherwise.
 * @param {PrivateProps} props The props.
 */
export function Private({children}) {
  const auth = useAuth();
  const [signingIn, setSigningIn] = useState(auth.pending);

  useEffect(() => {
    if (auth.pending || auth.error) {
      return;
    }

    if (!signingIn && !auth.user) {
      setSigningIn(true);
      auth.signIn();
      return;
    }
    if (signingIn && auth.user) {
      setSigningIn(false);
    }
  }, [auth, signingIn]);

  if (signingIn || auth.pending || auth.error || !auth.user) {
    return null;
  }

  return children;
}

/**
 * @typedef {Object} AuthProps
 * @property {AuthState} auth The auth state.
 */

/**
 * @template {AuthProps & Omit<{}, 'auth'>} P
 * @param {import('react').ComponentType<P>} Component The component to wrap.
 * @return {import('react').FC<Omit<P, 'auth'>>} The wrapped component.
 */
export function withAuth(Component) {
  /**
   * @param {Omit<P, 'auth'>} props The props.
   * @return {import('react').ReactElement} The element.
   */
  function Wrapped(props) {
    const auth = useAuth();
    return createElement(Component, /** @type {P} */ ({auth, ...props}));
  }
  Wrapped.displayName = 'withAuth' + (Component.displayName || Component.name);
  return Wrapped;
}
